import React from 'react';
import { Redirect } from 'react-router-dom';
import { Button, Container, Dimmer, Form, Icon, Label, Loader } from 'semantic-ui-react';
import ImageOnS3 from '../../../components/imageOnS3/imageOnS3.component';
import S3Manager from '../s3/s3manager';
import { BrandModel } from '../../../entities/brand.model';
import { ImageModel } from '../../../entities/image.model';
import MyForm from './form';
import MyFormState from './MyFormState';
import ImageCropperModal from '../../../components/image-cropper/imageCropper.modal';
import { buildBanner41WebpVariants, buildBannerXsWebp } from '../../../utils/imageVariants';

interface FormProps {
    match: any,
    history: any
}

interface BrandFormState extends MyFormState {
    cropOpen41: boolean;
    cropImageSrc41: string;
    cropOpen43: boolean;
    cropImageSrc43: string;
    cropKeyPrefix: string;
    cropSourceFileSize41: number | null;
    cropSourceFileSize43: number | null;
}

class BrandForm extends MyForm<FormProps, BrandFormState> {
    entity = 'brand'

    private trackUnsavedUploads = (...keys: Array<string | undefined>) => {
        keys.forEach((key) => {
            if (!key) return;
            if (!this.imagesUploaded.includes(key)) {
                this.imagesUploaded.push(key);
            }
        });
    }

    constructor(props: any) {
        super(props);
        this.state = {
            model: new BrandModel(),
            successMsg: "",
            errorMsg: "",
            loading: true,
            editing: false,
            editingSlug: false,
            slugValid: true,
            imageObj: ImageModel.empty(),
            imageObjs: { lg: ImageModel.empty(), sm: ImageModel.empty(), xs: ImageModel.empty(),
                image1: ImageModel.empty(), image2: ImageModel.empty(), image3: ImageModel.empty(),
                image4: ImageModel.empty(), image5: ImageModel.empty(), image6: ImageModel.empty()
            },
            redirect: "",
            cropOpen41: false,
            cropImageSrc41: '',
            cropOpen43: false,
            cropImageSrc43: '',
            cropKeyPrefix: '',
            cropSourceFileSize41: null,
            cropSourceFileSize43: null,
        }
    }

    getBannerKeyPrefix(): string | null {
        if (this.state.cropKeyPrefix) return this.state.cropKeyPrefix;
        const lg = this.state.model?.imageLgUrl;
        if (!lg || typeof lg !== 'string') return null;
        const base = lg.replace(/-lg\.webp$/i, '').replace(/-lg\.jpg$/i, '');
        return base.length > 0 ? base : null;
    }

    onBanner41Selected = (event: any) => {
        const file = event?.target?.files?.[0];
        if (!file) return;
        const src = URL.createObjectURL(file);
        const iso = new Date().toISOString();
        const prefix = `${this.entity}/${this.imageNamePrefix || 'brand'}-${iso}`;
        if (event?.target) event.target.value = '';
        this.setState({ cropOpen41: true, cropImageSrc41: src, cropKeyPrefix: prefix, errorMsg: '', cropSourceFileSize41: file.size });
    };

    onBanner43Selected = (event: any) => {
        const file = event?.target?.files?.[0];
        if (!file) return;
        const prefix = this.getBannerKeyPrefix();
        if (!prefix) {
            this.setState({ errorMsg: 'Эхлээд өргөн баннер (4:1) зураг оруулна уу.' });
            return;
        }
        const src = URL.createObjectURL(file);
        if (event?.target) event.target.value = '';
        this.setState({
            cropKeyPrefix: prefix,
            cropOpen43: true,
            cropImageSrc43: src,
            errorMsg: '',
            cropSourceFileSize43: file.size,
        });
    };

    pushOldWideBannerImagesForDelete() {
        if (this.state.imageObjs?.lg?.uploaded && this.state.imageObjs.lg.name) this.imagesToDelete.push(this.state.imageObjs.lg.name);
        if (this.state.imageObjs?.sm?.uploaded && this.state.imageObjs.sm.name) this.imagesToDelete.push(this.state.imageObjs.sm.name);
    }

    pushOldXsBannerImageForDelete() {
        if (this.state.imageObjs?.xs?.uploaded && this.state.imageObjs.xs.name) this.imagesToDelete.push(this.state.imageObjs.xs.name);
    }

    onBanner41Cropped = async (blob: Blob) => {
        const { cropKeyPrefix } = this.state;
        if (!cropKeyPrefix) return;
        this.pushOldWideBannerImagesForDelete();
        this.setState({ loading: true });
        try {
            const { lg, sm } = await buildBanner41WebpVariants(blob);
            const keyLg = `${cropKeyPrefix}-lg.webp`;
            const keySm = `${cropKeyPrefix}-sm.webp`;
            await S3Manager.uploadImage(keyLg, lg, false, 'image/webp');
            await S3Manager.uploadImage(keySm, sm, false, 'image/webp');
            this.trackUnsavedUploads(keyLg, keySm);
            this.recordSaved = false;
            this.setState({
                model: {
                    ...this.state.model,
                    imageLgUrl: keyLg,
                    imageSmUrl: keySm,
                },
                imageObjs: {
                    ...this.state.imageObjs,
                    lg: new ImageModel(undefined, keyLg, true, true),
                    sm: new ImageModel(undefined, keySm, true, true),
                },
                cropOpen41: false,
                cropImageSrc41: '',
                cropSourceFileSize41: null,
            });
        } catch (err) {
            console.error(err);
            this.setState({ errorMsg: 'Зураг боловсруулахад алдаа гарлаа' });
        } finally {
            this.setState({ loading: false });
        }
    };

    onBanner43Cropped = async (blob: Blob) => {
        const cropKeyPrefix = this.getBannerKeyPrefix();
        if (!cropKeyPrefix) return;
        this.pushOldXsBannerImageForDelete();
        this.setState({ loading: true });
        try {
            const xsBlob = await buildBannerXsWebp(blob);
            const keyXs = `${cropKeyPrefix}-xs.webp`;
            await S3Manager.uploadImage(keyXs, xsBlob, false, 'image/webp');
            this.trackUnsavedUploads(keyXs);
            this.recordSaved = false;
            this.setState({
                model: {
                    ...this.state.model,
                    imageXsUrl: keyXs,
                },
                imageObjs: {
                    ...this.state.imageObjs,
                    xs: new ImageModel(undefined, keyXs, true, true),
                },
                cropOpen43: false,
                cropImageSrc43: '',
                cropSourceFileSize43: null,
            });
        } catch (err) {
            console.error(err);
            this.setState({ errorMsg: 'Зураг боловсруулахад алдаа гарлаа' });
        } finally {
            this.setState({ loading: false });
        }
    };

    validate(): boolean {
        this.setState({ successMsg: ""});
        if (!this.state.model.name) {
            this.setState({ errorMsg: "Брэндийн нэрийг оруулна уу." });
            return false;
        }
        if (this.state.editingSlug || !this.state.slugValid || !this.state.model.slug) {
            this.setState({ errorMsg: "URL нэрийг шалгана уу." });
            return false;
        }
        if (!this.state.imageObjs.lg.file && !this.state.model.imageLgUrl) {
            this.setState({ errorMsg: "Өргөн баннер (4:1) зураг оруулна уу" });
            return false;
        }
        if (!this.state.imageObjs.sm.file && !this.state.model.imageSmUrl) {
            this.setState({ errorMsg: "Дунд хэмжээтэй зураг (4:1) — эхний тайралтын дараа үүснэ" });
            return false;
        }
        if (!this.state.imageObjs.xs.file && !this.state.model.imageXsUrl) {
            this.setState({ errorMsg: "Жижиг хэмжээтэй зураг (4:3) оруулна уу" });
            return false;
        }
        if (this.state.model.id !== undefined) {
            if (this.state.model.name === this.originalModel.name 
                && this.state.model.slug === this.originalModel.slug
                && this.state.model.info === this.originalModel.info
                && this.state.imageObjs.lg.name === this.originalModel.imageLgUrl
                && this.state.imageObjs.sm.name === this.originalModel.imageSmUrl
                && this.state.imageObjs.xs.name === this.originalModel.imageXsUrl
                && this.state.model.active === this.originalModel.active) {
                    this.setState({ errorMsg: "Өөрчлөлт байхгүй байна"});
                    return false;
                }
        }
        this.setState({ errorMsg: "" });
        return true;
    }

    render() {
        if (this.state.redirect) {
            return <Redirect to={this.state.redirect}/>
        }
        return (
            <Container>
                <Dimmer active={this.state.loading} inverted>
                    <Loader size='medium'></Loader>
                </Dimmer>
                <h2>{!this.state.editing ? 'Шинэ' : ''} Брэнд</h2>
                <Form>
                    <Form.Group>
                        { this.state.successMsg ? <div className="success-msg">{this.state.successMsg}</div> : null }
                        { this.state.errorMsg ? <div className="error-msg">{this.state.errorMsg}</div> : null }
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <Form.Input required label='Брэндийн нэр' value={this.state.model.name || ""}
                            onBlur={this.onNameBlur}
                            onChange={this.onNameChange} />
                    </Form.Group>
                    <Form.Group>
                        <div className="required field">
                            <label className="required">URL нэр</label>
                            { this.state.editingSlug ?
                            <React.Fragment>
                                <div className="flex-start">
                                    <Form.Input required value={this.state.model.slug}
                                        onBlur={this.onSlugBlur}
                                        onChange={this.onSlugChange} />
                                    { this.state.slugValid ? null :
                                        <React.Fragment>
                                            <Icon name="warning circle" color='red' size='large'/>
                                            <span className="error-msg">URL нэр ашиглагдсан байна</span>
                                        </React.Fragment>
                                    }
                                    &nbsp;&nbsp;
                                    <Button className="cursor" color='orange'
                                        disabled={this.originalModel.slug === this.state.model.slug}
                                        onClick={this.postNewSlug}>Шалгах</Button>
                                    <Button className="cursor" onClick={this.cancelSlugChange}>Болих</Button>
                                </div>
                                <div>
                                    <br/>
                                    <Icon name="info circle" color="blue" />
                                    Зөвхөн англи үсэг болон - тэмдэг ашиглана
                                </div>
                            </React.Fragment>
                            :
                            <div>
                                <Label>{`${process.env.REACT_APP_DOMAIN}/brand/${this.state.model.slug}`}</Label>
                                { this.state.slugValid ?
                                    (this.state.model.slug && this.state.model.slug.length > 0) ?
                                        <Icon name="check circle" color='green' size='large'/> : null :
                                    <Icon name="warning circle" color='red' size='large'/>
                                }
                                &nbsp;&nbsp;
                                <Icon className="cursor" name="edit outline" size='large' onClick={this.toggleSlug}/>
                            </div>
                        }
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.TextArea label='Тайлбар' width="16" value={this.state.model.info} onChange={this.onInfoChange} />
                    </Form.Group>
                    <Form.Group className="flex-space-between">
                        <div className="field">
                            <label>
                                Том хэмжээтэй зураг (4:1) ба дунд хэмжээтэй зураг (4:1)
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            {this.state.model.imageLgUrl ? (
                                <span><small>Том</small><br /><ImageOnS3 imgKey={this.state.model.imageLgUrl} width='160px' height='40px' /></span>
                            ) : null}
                        </div>
                        <Form.Input control='input' type="file" accept="image/png, image/jpeg, image/webp" onChange={this.onBanner41Selected} />
                    </Form.Group>
                    <Form.Group className="flex-space-between">
                        <div className="field">
                            <label>
                                Жижиг хэмжээтэй зураг (4:3)
                            </label>
                        </div>
                        {this.state.model.imageXsUrl ? (
                            <ImageOnS3 imgKey={this.state.model.imageXsUrl} width='120px' height='90px' />
                        ) : null}
                        <Form.Input control='input' type="file" accept="image/png, image/jpeg, image/webp" onChange={this.onBanner43Selected} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Checkbox checked={this.state.model.active} onChange={this.onActiveChange} label='Идэвхитэй' />
                    </Form.Group>
                    <br/>
                    <Form.Button disabled={this.state.loading} onClick={() => this.save()}>Хадгалах</Form.Button>
                </Form>
                <ImageCropperModal
                    key="brand-banner-crop-41"
                    open={this.state.cropOpen41}
                    imageSrc={this.state.cropImageSrc41}
                    onClose={() => this.setState({ cropOpen41: false, cropImageSrc41: '', cropSourceFileSize41: null })}
                    onCropped={this.onBanner41Cropped}
                    aspect={4}
                    outputWidth={3200}
                    outputHeight={800}
                    cropTitle="Том / дунд зураг тайрах (4:1)"
                    sourceFileSizeBytes={this.state.cropSourceFileSize41 ?? undefined}
                />
                <ImageCropperModal
                    key={`brand-banner-crop-43-${this.state.cropImageSrc43 || 'closed'}`}
                    open={this.state.cropOpen43}
                    imageSrc={this.state.cropImageSrc43}
                    onClose={() => this.setState({ cropOpen43: false, cropImageSrc43: '', cropSourceFileSize43: null })}
                    onCropped={this.onBanner43Cropped}
                    aspect={4 / 3}
                    outputWidth={1600}
                    outputHeight={1200}
                    cropTitle="Жижиг зураг тайрах (4:3)"
                    sourceFileSizeBytes={this.state.cropSourceFileSize43 ?? undefined}
                />
            </Container>
        );
    }
}

export default BrandForm;